import { useEffect, useState } from 'react'
import Icon from './Icon'
import {
  registrarMovimentacao,
  getMovimentacoes,
  type CaixaMovimentacao,
} from '../api/client'

/**
 * CaixaMovimentacaoModal — Sangria e Suprimento (D-119, 2026-05-17).
 *
 * Conceitos:
 *   - SANGRIA: caixa retira dinheiro do caixa durante o turno (vai pro cofre/banco).
 *     Reduz o "esperado em caixa" no fechamento.
 *   - SUPRIMENTO: caixa coloca dinheiro extra fora de venda (mais troco).
 *     Aumenta o "esperado em caixa".
 *
 * Acessível pelo NavBar quando perfil=caixa+turno aberto.
 * Lista movimentações já registradas + form pra registrar nova.
 */
export default function CaixaMovimentacaoModal({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const [tipo, setTipo] = useState<'sangria' | 'suprimento'>('sangria')
  const [valorTexto, setValorTexto] = useState('')
  const [motivo, setMotivo] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [sucesso, setSucesso] = useState('')
  const [historico, setHistorico] = useState<CaixaMovimentacao[]>([])
  const [totais, setTotais] = useState({ sangrias: 0, suprimentos: 0, saldo_liquido: 0 })
  const [loading, setLoading] = useState(false)

  function carregar() {
    setLoading(true)
    getMovimentacoes()
      .then(r => { setHistorico(r.movimentacoes); setTotais(r.totais) })
      .catch(() => { /* sem turno aberto = vazio, normal */ })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (!open) return
    setError(''); setSucesso(''); setValorTexto(''); setMotivo('')
    carregar()
  }, [open])

  // ESC fecha
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  function parseValorCentavos(s: string): number | null {
    if (!s.trim()) return null
    const norm = s.trim().replace(/\./g, '').replace(',', '.')
    const num = Number(norm)
    if (!Number.isFinite(num) || num <= 0) return null
    return Math.round(num * 100)
  }

  async function handleRegistrar() {
    setError(''); setSucesso('')
    const cents = parseValorCentavos(valorTexto)
    if (cents === null) {
      setError('Valor inválido. Use formato R$ 0,00 (maior que zero).')
      return
    }
    if (!motivo.trim()) {
      setError('Motivo é obrigatório (pra auditoria do recibo).')
      return
    }
    setSubmitting(true)
    try {
      await registrarMovimentacao({ tipo, valor_centavos: cents, motivo: motivo.trim() })
      setSucesso(`${tipo === 'sangria' ? 'Sangria' : 'Suprimento'} registrado: R$ ${(cents / 100).toFixed(2).replace('.', ',')}`)
      setValorTexto('')
      setMotivo('')
      carregar()
    } catch (e: any) {
      setError(e?.message || 'Erro ao registrar movimentação')
    } finally {
      setSubmitting(false)
    }
  }

  const fmtBRL = (c: number) => `R$ ${(c / 100).toFixed(2).replace('.', ',')}`

  return (
    <div className="fixed inset-0 z-50 bg-carvao/40 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div className="glass-modal w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl flex flex-col max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-3 border-b border-white/40 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.16em] text-terracota/70 font-medium">Caixa</p>
            <h2 className="font-serif text-lg font-semibold text-carvao">Sangria & Suprimento</h2>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 transition"
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        {/* Form */}
        <div className="px-5 py-4 border-b border-gray-200/60 bg-white/50">
          <div className="grid grid-cols-2 gap-2 mb-3">
            <button
              onClick={() => setTipo('sangria')}
              disabled={submitting}
              className={`py-2.5 rounded-lg text-sm font-medium transition border ${
                tipo === 'sangria'
                  ? 'bg-red-600 text-white border-red-700'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-red-400'
              }`}
            >
              <div className="text-[10px] uppercase tracking-wider opacity-70">Tirar dinheiro</div>
              <div>Sangria</div>
            </button>
            <button
              onClick={() => setTipo('suprimento')}
              disabled={submitting}
              className={`py-2.5 rounded-lg text-sm font-medium transition border ${
                tipo === 'suprimento'
                  ? 'bg-emerald-600 text-white border-emerald-700'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-emerald-400'
              }`}
            >
              <div className="text-[10px] uppercase tracking-wider opacity-70">Colocar dinheiro</div>
              <div>Suprimento</div>
            </button>
          </div>

          <div className="mb-3">
            <label className="block text-xs text-gray-600 mb-1.5 font-medium">Valor</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">R$</span>
              <input
                type="text"
                inputMode="decimal"
                value={valorTexto}
                onChange={e => setValorTexto(e.target.value)}
                placeholder="0,00"
                disabled={submitting}
                className="w-full border border-gray-300 rounded-lg pl-10 pr-3 py-2.5 text-base font-mono tabular-nums focus:border-terracota focus:outline-none"
              />
            </div>
          </div>

          <div className="mb-3">
            <label className="block text-xs text-gray-600 mb-1.5 font-medium">Motivo (obrigatório)</label>
            <input
              type="text"
              value={motivo}
              onChange={e => setMotivo(e.target.value)}
              disabled={submitting}
              placeholder={tipo === 'sangria' ? 'Ex: levei pro banco' : 'Ex: troco de R$ 200'}
              maxLength={200}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-terracota focus:outline-none"
            />
          </div>

          {error && (
            <div className="border border-red-200 bg-red-50 rounded-lg p-2.5 mb-2 text-xs text-red-800">{error}</div>
          )}
          {sucesso && (
            <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-2.5 mb-2 text-xs text-emerald-800">{sucesso}</div>
          )}

          <button
            onClick={handleRegistrar}
            disabled={submitting}
            className={`w-full rounded-lg py-2.5 text-sm font-semibold text-white transition disabled:opacity-50 ${
              tipo === 'sangria' ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'
            }`}
          >
            {submitting ? 'Registrando…' : `Registrar ${tipo === 'sangria' ? 'sangria' : 'suprimento'}`}
          </button>
        </div>

        {/* Histórico */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          <div className="flex items-baseline justify-between mb-2">
            <p className="text-[10px] uppercase tracking-[0.14em] text-gray-500 font-semibold">Movimentações deste turno</p>
            <button onClick={carregar} className="text-[11px] text-gray-500 hover:text-terracota" disabled={loading}>
              {loading ? '...' : 'Atualizar'}
            </button>
          </div>

          {historico.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">Nenhuma movimentação ainda neste turno.</p>
          ) : (
            <>
              <div className="space-y-1 mb-3">
                {historico.map(m => (
                  <div key={m.id} className="border border-gray-200 rounded-lg p-2 bg-white text-xs">
                    <div className="flex items-center justify-between">
                      <span className={`uppercase font-semibold tracking-wider text-[10px] ${m.tipo === 'sangria' ? 'text-red-600' : 'text-emerald-600'}`}>
                        {m.tipo === 'sangria' ? '↓ Sangria' : '↑ Suprimento'}
                      </span>
                      <span className="font-mono tabular-nums font-semibold">{fmtBRL(m.valor_centavos)}</span>
                    </div>
                    {m.motivo && <p className="text-gray-600 mt-1">{m.motivo}</p>}
                    <p className="text-[10px] text-gray-400 mt-1">{m.criada_em} · {m.criada_por_nome ?? '—'}</p>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-200 pt-2 space-y-1 text-xs">
                <div className="flex justify-between"><span className="text-gray-600">Total sangrias (saída)</span><span className="font-mono tabular-nums text-red-600">- {fmtBRL(totais.sangrias)}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">Total suprimentos (entrada)</span><span className="font-mono tabular-nums text-emerald-600">+ {fmtBRL(totais.suprimentos)}</span></div>
                <div className="flex justify-between font-medium border-t border-gray-100 pt-1"><span>Saldo líquido</span><span className={`font-mono tabular-nums ${totais.saldo_liquido < 0 ? 'text-red-700' : 'text-emerald-700'}`}>{totais.saldo_liquido >= 0 ? '+' : '-'} {fmtBRL(Math.abs(totais.saldo_liquido))}</span></div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
